const THROTTLE_LIMIT = 1000;
const DEBOUNCE_LIMIT = 300;

export * from './download';
export * from './export-to-csv';
export * from './focus-first-available';
export * from './format-address';
export * from './format-boolean';
export * from './format-currency';
export * from './format-date';
export * from './format-date-diff';
export * from './format-name';
export * from './format-number';
export * from './format-percentage';
export * from './format-phone';
export * from './format-plural';
export * from './format-string';
export * from './get-date-diff';
export * from './is-equal';
export * from './rbac-ui-gating';

export const EMPTY_OBJECT = Object.freeze({}) as never;

export const EMPTY_ARRAY = Object.freeze([]) as never[];

export const EMPTY_PLACEHOLDER = '--';

export const NOOP = Object.freeze(() => {});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function throttle(func: (args?: any) => void, limit = THROTTLE_LIMIT) {
  let isThrottle = false;
  return (...args) => {
    if (!isThrottle) {
      func(...args);
      isThrottle = true;
      setTimeout(() => (isThrottle = false), limit);
    }
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounce(func: (...args: any) => void, wait = DEBOUNCE_LIMIT) {
  let timeout: number | undefined;

  return function executedFunction(...args) {
    const later = () => {
      timeout = undefined;
      func.call(this, ...args);
    };

    clearTimeout(timeout);
    timeout = window.setTimeout(later, wait);
  };
}

export function normalizeValue(value: string) {
  return value.replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 *
 * @returns A random unique identifier
 */
export function generateId(): string {
  let id;

  while (!id) {
    id = Math.random();
  }

  return id.toString(36).substring(2);
}

// TODO(OF-2441): rename to something better like `toTitleCase()`
/**
 *
 * @returns An enumerated value to a friendly display label
 */
export function enumToLabel(label: string): string {
  return label
    .toLocaleLowerCase()
    .split('_')
    .map((word) => `${word.substring(0, 1).toUpperCase()}${word.substring(1)}`)
    .join(' ');
}

/**
 *
 * @returns Entries serialized as a JS object
 */
export function entriesToObject(
  entries: FormData | URLSearchParams,
): Record<string, string | string[]> {
  const json = {};

  entries.forEach((value, key) => {
    if (key in json) {
      if (!Array.isArray(json[key])) {
        json[key] = [json[key]];
      }

      json[key].push(value);
    } else {
      json[key] = value;
    }
  });

  return json;
}

/**
 *
 * @param {unknown} value Any value to check if it's nullish
 * @returns {boolean} A boolean indicating if `value` is either null or undefined
 */
export const isNullish = (value: unknown) => value === null || value === undefined;
